import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import type { NavigationProp } from '@react-navigation/native';
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  SafeAreaView,
  Platform,
  Linking
} from 'react-native';
import tw from 'twrnc';

import { ENV_CONFIG } from '../../../config/environment';
import { supabase } from '../../../lib/supabase';
import type { RootStackParamList } from '../../../navigation/types';
import { useGuestUser } from '../../../shared/contexts/GuestUserContext';
import { useAuth } from '../../../shared/hooks/useAuth';
import { getPersonalPlans } from '../config/subscriptionPlans';
import { UserSubscription } from '../services/entitlementService';
import { paymentService } from '../services/paymentService';
import { subscriptionService } from '../services/subscriptionService';
import { subscriptionStateManager } from '../services/subscriptionStateManager';
import { normalizeSubscriptionTier, PREMIUM_FAMILY_MEMBER_LIMIT, SubscriptionPlan, SubscriptionTier } from '../types/premiumFeatures';

import { BillingIntervalCard } from './BillingIntervalCard';
import { clearFeatureAccessCache } from './PremiumFeatureGate';
import { uiText } from '../../../shared/utils/uiText';

interface SubscriptionPlansScreenProps {
  route?: {
    params?: {
      preselectedPlan?: string;
      fromGuestUpgrade?: boolean;
    };
  };
  onSubscriptionSuccess?: () => void;
}

export const getVisibleConsumerPlans = (
  availablePlans: SubscriptionPlan[],
  _subscription: UserSubscription | null,
): SubscriptionPlan[] => {
  return getPersonalPlans().filter((plan) => availablePlans.some((availablePlan) => availablePlan.id === plan.id));
};

const getEffectiveTier = (subscription: UserSubscription | null): SubscriptionTier =>
  normalizeSubscriptionTier(subscription?.subscriptionType);

const isInheritedPremium = (subscription: UserSubscription | null): boolean =>
  subscription?.metadata?.premiumAccessSource === 'family_inherited';

const normalizePlanSelection = (planId?: string | null): string | null => {
  if (!planId) {
    return null;
  }

  return ['premium', 'individual', 'family', 'group_small', 'group_medium', 'group_large'].includes(planId)
    ? 'premium'
    : planId;
};

export const SubscriptionPlansScreen: React.FC<SubscriptionPlansScreenProps> = ({ route, onSubscriptionSuccess }) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscription | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const { user } = useAuth();
  const { isGuest } = useGuestUser();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();

  // Check if this is from guest upgrade flow
  const fromGuestUpgrade = route?.params?.fromGuestUpgrade || false;

  // Check if monetization is enabled
  const monetizationEnabled = ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED;
  const isPreviewMode = !monetizationEnabled;

  useEffect(() => {
    loadSubscriptionData();
  }, [user]);

  useEffect(() => {
    // Pre-select plan if specified in route params
    if (route?.params?.preselectedPlan) {
      setSelectedPlan(normalizePlanSelection(route.params.preselectedPlan));
    }
  }, [route?.params?.preselectedPlan]);

  const loadSubscriptionData = async () => {
    console.log('🔄 [DEBUG] Starting loadSubscriptionData...');
    console.log('🔄 [DEBUG] User ID:', user?.id);
    console.log('🔄 [DEBUG] Preview mode:', isPreviewMode);

    setLoading(true);
    try {
      // Get available plans
      const availablePlans = isPreviewMode
        ? subscriptionService.getPreviewPlans()
        : subscriptionService.getAvailablePlans();

      console.log('🔄 [DEBUG] Available plans:', availablePlans.length);

      // Get user's current subscription first
      let subscription = null;
      if (user?.id) {
        console.log('🔄 [DEBUG] Fetching subscription for user:', user.id);
        subscription = await subscriptionService.getUserSubscription(user.id);
        console.log('🔄 [DEBUG] Fetched subscription:', subscription);

        setCurrentSubscription(subscription);
        console.log('🔄 [DEBUG] Set currentSubscription state:', subscription?.subscriptionType);
      }

      const effectiveTier = getEffectiveTier(subscription);
      const hasActiveSubscription = !!subscription && effectiveTier !== SubscriptionTier.FREE;
      const filteredPlans = getVisibleConsumerPlans(availablePlans, subscription);

      console.log('🔄 [DEBUG] Filtered plans:', filteredPlans.length);
      console.log('🔄 [DEBUG] Has active subscription:', hasActiveSubscription);
      setPlans(filteredPlans);

      // Pre-select current plan if exists
      if (subscription && !selectedPlan) {
        const currentPlan = filteredPlans.find((plan) => plan.tier === effectiveTier);
        console.log('🔄 [DEBUG] Current plan found:', currentPlan?.name);
        if (currentPlan) {
          setSelectedPlan(currentPlan.id);
          console.log('🔄 [DEBUG] Pre-selected plan:', currentPlan.id);
        }
      }

      // Select most popular plan by default if none selected
      if (!selectedPlan && filteredPlans.length > 0) {
        const popularPlan = filteredPlans.find(p => p.popular) || filteredPlans[0];
        setSelectedPlan(popularPlan.id);
        console.log('🔄 [DEBUG] Selected default plan:', popularPlan.id);
      }

      console.log('🔄 [DEBUG] loadSubscriptionData completed successfully');
    } catch (error) {
      console.error('❌ [DEBUG] Failed to load subscription data:', error);
      Alert.alert(uiText('errorTitle'), uiText('plansLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) return;

    if (!user?.id) {
      Alert.alert(
        uiText('signInRequired'),
        uiText('stripeSignInMessage'),
        [{ text: uiText('ok') }]
      );
      return;
    }

    await handleSubscribeForPlan(selectedPlan);
  };

  const handleSubscribeForPlan = async (planId: string) => {
    console.log('🎯 Plan selection started:', planId);
    console.log('👤 Is guest:', isGuest);

    // iOS MUST use native In-App Purchase (App Store policy)
    if (Platform.OS === 'ios') {
      Alert.alert(
        'iOS Subscription Required',
        'On iOS, subscriptions must be purchased through the App Store. Please use the native subscription screen.',
        [{ text: uiText('ok') }]
      );
      return;
    }

    try {
      setSubscribing(true);

      let currentUser = user;

      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      console.log('👤 Getting current user...');
      const { data: { user: existingUser } } = await supabase.auth.getUser();
      if (!existingUser) {
        throw new Error('User not authenticated');
      }
      currentUser = existingUser;
      console.log('✅ User authenticated');

      // Determine payment provider
      const provider = paymentService.getPreferredProvider();
      console.log('💳 Payment provider:', provider);

      console.log('🛒 Creating subscription with:', {
        userId: currentUser.id,
        planId: planId,
        billingInterval: billingInterval,
        provider: provider,
        customerEmail: currentUser.email,
      });

      const result = await paymentService.createSubscription({
        userId: currentUser.id,
        planId: planId,
        billingInterval: billingInterval,
        provider: provider,
        customerEmail: currentUser.email
      });

      console.log('📊 Subscription result:', result);

      if (result.success) {
        const checkoutUrl = result.checkoutUrl ||
          (typeof result.nextAction?.url === 'string' ? result.nextAction.url : undefined);

        if (checkoutUrl) {
          await Linking.openURL(checkoutUrl);
          return;
        }

        console.log('✅ [SUCCESS] Subscription created successfully, refreshing subscription data...');

        // Clear the feature access cache so the new subscription is recognized immediately
        if (currentUser?.id) {
          clearFeatureAccessCache(currentUser.id);
        }

        // Trigger immediate state refresh across the entire app
        await subscriptionStateManager.refreshAllSubscriptionData();

        // Small delay to allow database replication, then refresh local subscription data
        setTimeout(async () => {
          await loadSubscriptionData();
          console.log('✅ [SUCCESS] Subscription data refreshed after delay');
        }, 1000);

        console.log('✅ [SUCCESS] Subscription refresh scheduled, showing success alert');

        const successMessage = isPreviewMode
          ? uiText('previewSuccess')
          : uiText('welcomeSubscription');

        Alert.alert(
          uiText('successBang'),
          successMessage,
          [
            {
              text: uiText('ok'), onPress: () => {
                if (onSubscriptionSuccess) {
                  onSubscriptionSuccess();
                } else {
                  navigation.goBack();
                }
              }
            }
          ]
        );
      } else {
        throw new Error(result.error || 'Payment failed');
      }
    } catch (error) {
      console.error('Subscription purchase failed:', error);
      Alert.alert(
        uiText('purchaseFailedTitle'),
        error instanceof Error ? error.message : uiText('processSubscriptionFailed'),
        [
          { text: uiText('ok') }
        ]
      );
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancel = async () => {
    console.log('[DEBUG] Cancel button clicked');
    console.log('[DEBUG] User ID:', user?.id);
    console.log('[DEBUG] Current subscription:', currentSubscription);
    console.log('[DEBUG] Loading state:', loading);

    if (!user?.id || !currentSubscription) {
      console.log('[DEBUG] Early return - missing user or subscription');
      return;
    }

    console.log('[DEBUG] Showing cancel confirmation dialog');
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = async () => {
    console.log('[DEBUG] User confirmed cancellation');
    setShowCancelConfirm(false);
    setLoading(true);

    if (!user?.id) {
      Alert.alert(uiText('errorTitle'), uiText('userNotFoundTryAgain'));
      setLoading(false);
      return;
    }

    try {
      const success = await paymentService.cancelSubscription(user.id, true);
      if (success) {
        // Clear caches and trigger immediate state refresh across the app
        clearFeatureAccessCache(user.id);
        await subscriptionStateManager.refreshAllSubscriptionData();

        Alert.alert(uiText('subscriptionCanceled'), uiText('subscriptionCanceledMessage'));
        await loadSubscriptionData(); // Refresh data
      } else {
        Alert.alert(uiText('errorTitle'), uiText('cancelFailed'));
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      Alert.alert(uiText('errorTitle'), uiText('unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  const getAnnualSavings = (plan: SubscriptionPlan): number => {
    const monthlyTotal = plan.price.monthly * 12;
    const annualPrice = plan.price.annual;
    const savings = monthlyTotal - annualPrice;
    return Math.round((savings / monthlyTotal) * 100);
  };

  if (loading) {
    return (
      <SafeAreaView style={tw`flex-1 bg-gray-50`}>
        <View style={tw`flex-1 items-center justify-center`}>
          <ActivityIndicator size="large" color="#4f46e5" />
          <Text style={tw`mt-4 text-gray-600`}>{uiText('loadingPlans')}</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={tw`flex-1 bg-gray-50`}>
      <ScrollView style={tw`flex-1`} contentContainerStyle={tw`p-6`}>
        {/* Header */}
        <View style={tw`mb-6`}>
          <Text style={tw`text-2xl font-bold text-gray-800 mb-2`}>
            Premium
          </Text>
          <Text style={tw`text-gray-600`}>
            One subscription unlocks premium play for you and your household.
          </Text>
        </View>

        {/* Preview Mode Banner */}
        {isPreviewMode && (
          <View style={tw`bg-blue-100 p-4 rounded-lg mb-6 border border-blue-200`}>
            <View style={tw`flex-row items-center mb-2`}>
              <Ionicons name="information-circle" size={20} color="#1e40af" />
              <Text style={tw`text-blue-800 font-bold text-lg ml-2`}>{uiText('previewMode')}</Text>
            </View>
            <Text style={tw`text-blue-700`}>
              Monetization features are currently disabled. This is a preview of the subscription plans that will be available when enabled.
            </Text>
          </View>
        )}

        {/* Guest Upgrade Banner */}
        {isGuest && fromGuestUpgrade && (
          <View style={tw`bg-gradient-to-r from-purple-100 to-blue-100 p-5 rounded-2xl mb-6 border-2 border-purple-200`}>
            <View style={tw`flex-row items-start mb-3`}>
              <View style={tw`w-10 h-10 bg-purple-500 rounded-full items-center justify-center mr-3`}>
                <Ionicons name="rocket" size={24} color="white" />
              </View>
              <View style={tw`flex-1`}>
                <Text style={tw`text-purple-900 font-bold text-lg mb-1`}>
                  Unlock Friends & Family Play
                </Text>
                <Text style={tw`text-purple-800 text-sm leading-5`}>
                  Sign in or create an account, then continue to secure Stripe Checkout. Your guest progress can be saved after sign-in.
                </Text>
              </View>
            </View>
            <View style={tw`bg-white bg-opacity-60 p-3 rounded-xl`}>
              <View style={tw`flex-row items-center mb-2`}>
                <Ionicons name="checkmark-circle" size={18} color="#7c3aed" style={tw`mr-2`} />
                <Text style={tw`text-purple-900 text-sm font-medium`}>{uiText('secureCheckout')}</Text>
              </View>
              <View style={tw`flex-row items-center`}>
                <Ionicons name="checkmark-circle" size={18} color="#7c3aed" style={tw`mr-2`} />
                <Text style={tw`text-purple-900 text-sm font-medium`}>{uiText('noCardDetails')}</Text>
              </View>
            </View>
          </View>
        )}

        {/* Billing Interval Toggle */}
        <View style={tw`flex-row bg-gray-200 rounded-lg p-1 mb-6`}>
          <TouchableOpacity 
            style={[
              tw`flex-1 py-3 rounded-md`,
              billingInterval === 'monthly' && tw`bg-white shadow-sm`
            ]}
            onPress={() => setBillingInterval('monthly')}
          >
            <Text style={[
              tw`text-center font-medium`,
              billingInterval === 'monthly' ? tw`text-blue-600` : tw`text-gray-600`
            ]}>
              Monthly
            </Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[
              tw`flex-1 py-3 rounded-md`,
              billingInterval === 'annual' && tw`bg-white shadow-sm`
            ]}
            onPress={() => setBillingInterval('annual')}
          >
            <Text style={[
              tw`text-center font-medium`,
              billingInterval === 'annual' ? tw`text-blue-600` : tw`text-gray-600`
            ]}>
              Annual
            </Text>
            {plans.length > 0 && (
              <Text style={tw`text-center text-xs text-green-600 font-medium`}>
                Save {getAnnualSavings(plans[0])}%
              </Text>
            )}
          </TouchableOpacity>
        </View>
        
        {/* Current Subscription Status */}
        {currentSubscription && (
          <View style={tw`bg-green-100 p-4 rounded-lg mb-6 border border-green-200`}>
            <View style={tw`flex-row items-center mb-2`}>
              <Ionicons name="checkmark-circle" size={20} color="#059669" />
              <Text style={tw`text-green-800 font-bold ml-2`}>{uiText('currentSubscription')}</Text>
            </View>
            <Text style={tw`text-green-700`}>
              {isInheritedPremium(currentSubscription) ? 'Premium shared access' : 'Premium'}
            </Text>
            <Text style={tw`text-green-600 text-sm mt-1`}>
              {getEffectiveTier(currentSubscription) === SubscriptionTier.FREE
                ? 'Free access with daily quiz limits'
                : isInheritedPremium(currentSubscription)
                ? `Included through the Premium account owner. Up to ${PREMIUM_FAMILY_MEMBER_LIMIT} invited members can share this subscription.`
                : currentSubscription.currentPeriodEnd && !isNaN(currentSubscription.currentPeriodEnd.getTime())
                ? `Renews on ${currentSubscription.currentPeriodEnd.toLocaleDateString()}`
                : 'No renewal date'}
            </Text>
            {currentSubscription.cancelAtPeriodEnd && (
              <Text style={tw`text-orange-700 mt-2 font-medium`}>
                ⚠️ Your subscription will end on the renewal date.
              </Text>
            )}
            {isInheritedPremium(currentSubscription) && (
              <Text style={tw`text-green-700 mt-2 font-medium`}>
                Billing is managed by the Premium account owner.
              </Text>
            )}
          </View>
        )}

        <View style={tw`bg-white p-4 rounded-lg mb-6 border border-gray-200`}>
          <Text style={tw`text-gray-900 font-semibold mb-2`}>
            Premium includes your household
          </Text>
          <Text style={tw`text-gray-600 leading-5`}>
            One Premium subscription unlocks unlimited quiz play, private multiplayer, full progress, and up to {PREMIUM_FAMILY_MEMBER_LIMIT} invited family members.
          </Text>
        </View>

        {/* Billing Interval Change Options for current Premium owner */}
        {currentSubscription &&
          getEffectiveTier(currentSubscription) !== SubscriptionTier.FREE &&
          !isInheritedPremium(currentSubscription) && (
          <View style={tw`mb-6`}>
            {(() => {
              const currentPlan = plans.find((plan) => plan.tier === SubscriptionTier.PREMIUM);
              const currentBillingInterval = currentSubscription.billingInterval || 'monthly';
              const alternateBillingInterval = currentBillingInterval === 'monthly' ? 'annual' : 'monthly';

              if (!currentPlan) return null;

              // Calculate if switching would actually save money
              const currentPrice = currentPlan.price[currentBillingInterval];
              const alternatePrice = currentPlan.price[alternateBillingInterval];

              // Calculate annual cost for both billing intervals
              const currentAnnualCost = currentBillingInterval === 'monthly'
                ? currentPrice * 12
                : currentPrice;
              const alternateAnnualCost = alternateBillingInterval === 'monthly'
                ? alternatePrice * 12
                : alternatePrice;

              const wouldSaveMoney = alternateAnnualCost < currentAnnualCost;
              const isUpgradeToAnnual = currentBillingInterval === 'monthly' && alternateBillingInterval === 'annual';

              // Check if user is near renewal (within 30 days) for flexibility options
              const isNearRenewal = (() => {
                if (!currentSubscription.currentPeriodEnd) return false;
                const expiryDate = new Date(currentSubscription.currentPeriodEnd);
                const now = new Date();
                const daysUntilRenewal = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                return daysUntilRenewal <= 30;
              })();

              // Only show billing interval changes if:
              // 1. Switching would save money (monthly → annual), OR
              // 2. User is near renewal and wants flexibility (annual → monthly)
              const shouldShow = wouldSaveMoney || (isNearRenewal && !isUpgradeToAnnual);

              return shouldShow ? (
                <View style={tw`mb-6`}>
                  <Text style={tw`text-xl font-bold text-gray-800 mb-4`}>
                    {wouldSaveMoney
                      ? '💡 Save Money with Annual Billing'
                      : isNearRenewal
                      ? '🔄 Renewal Options - Change Billing Frequency'
                      : '🔄 Change Billing Frequency'
                    }
                  </Text>
                  <BillingIntervalCard
                    currentPlan={currentPlan}
                    currentBillingInterval={currentBillingInterval}
                    targetBillingInterval={alternateBillingInterval}
                    onChangeComplete={() => {
                      loadSubscriptionData();
                      if (onSubscriptionSuccess) {
                        onSubscriptionSuccess();
                      }
                    }}
                  />
                </View>
              ) : null;
            })()}
          </View>
        )}

        {/* Plans List - Only show for new subscribers or in preview mode */}
        {(!currentSubscription || getEffectiveTier(currentSubscription) === SubscriptionTier.FREE || isPreviewMode) && plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            selected={selectedPlan === plan.id}
            onSelect={() => {
              console.log('🎯 [CLICK] Plan card clicked:', plan.name, plan.id);
              console.log('🎯 [CLICK] Current subscription state:', {
                currentSubscription: currentSubscription,
                subscriptionType: currentSubscription?.subscriptionType,
                status: currentSubscription?.status,
                id: currentSubscription?.id
              });
              console.log('🎯 [CLICK] Plan tier:', plan.tier);
              console.log('🎯 [CLICK] Loading state:', loading);
              console.log('🎯 [CLICK] User ID:', user?.id);
              console.log('🎯 [CLICK] Is different plan?', getEffectiveTier(currentSubscription) !== plan.tier);

              setSelectedPlan(plan.id);
            }}
            billingInterval={billingInterval}
            isPreviewMode={isPreviewMode}
            isCurrentPlan={getEffectiveTier(currentSubscription) === plan.tier}
          />
        ))}

        {/* Terms of Use and Privacy Policy Links */}
        <View style={tw`mt-6 mb-4`}>
          <Text style={tw`text-gray-500 text-xs text-center mb-2`}>
            By subscribing, you agree to our:
          </Text>
          <View style={tw`flex-row justify-center items-center gap-2`}>
            <TouchableOpacity onPress={() => Linking.openURL('https://kingdombiblequiz.com/terms')}>
              <Text style={tw`text-blue-600 text-xs underline`}>{uiText('termsOfUse')}</Text>
            </TouchableOpacity>
            <Text style={tw`text-gray-400 text-xs`}>•</Text>
            <TouchableOpacity onPress={() => Linking.openURL('https://kingdombiblequiz.com/privacy')}>
              <Text style={tw`text-blue-600 text-xs underline`}>{uiText('privacyPolicy')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
      
      {/* Action Buttons */}
      {currentSubscription ? (
        <View style={tw`p-6 bg-white border-t border-gray-200`}>
          <View style={tw`flex-row gap-3`}>
            {(() => {
              console.log('[DEBUG] Rendering cancel button section');
              console.log('[DEBUG] currentSubscription.cancelAtPeriodEnd:', currentSubscription.cancelAtPeriodEnd);
              console.log('[DEBUG] Will show cancel button:', !currentSubscription.cancelAtPeriodEnd);
              return null;
            })()}
            {!currentSubscription.cancelAtPeriodEnd &&
              getEffectiveTier(currentSubscription) !== SubscriptionTier.FREE &&
              !isInheritedPremium(currentSubscription) && (
              <TouchableOpacity
                style={tw`flex-1 bg-red-100 py-4 rounded-lg border border-red-200`}
                onPress={() => {
                  console.log('[DEBUG] Cancel button onPress triggered');
                  handleCancel();
                }}
                disabled={loading}
              >
                <Text style={tw`text-red-600 font-bold text-center`}>{uiText('cancelPlan')}</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={tw`flex-1 bg-blue-600 py-4 rounded-lg`}
              onPress={() => {
                if (onSubscriptionSuccess) {
                  onSubscriptionSuccess();
                } else {
                  navigation.goBack();
                }
              }}
            >
              <Text style={tw`text-white font-bold text-center`}>{uiText('done')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : selectedPlan ? (
        <View style={tw`p-6 bg-white border-t border-gray-200 shadow-lg`}>
          <TouchableOpacity
            style={[
              tw`bg-blue-600 py-5 rounded-xl shadow-sm`,
              subscribing && tw`opacity-50`
            ]}
            onPress={handleSubscribe}
            disabled={subscribing}
          >
            {subscribing ? (
              <ActivityIndicator color="white" />
            ) : (
              <View>
                <Text style={tw`text-white font-bold text-center text-lg mb-1`}>
                  {isPreviewMode ? 'Preview Payment' : 'Continue to Payment'}
                </Text>
                <Text style={tw`text-white text-center text-sm opacity-90`}>
                  {(() => {
                    const plan = plans.find(p => p.id === selectedPlan);
                    const price = billingInterval === 'annual' ? plan?.price.annual : plan?.price.monthly;
                    return `$${price}/${billingInterval === 'annual' ? 'year' : 'month'}`;
                  })()}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
      ) : null}

      {/* Custom Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <View style={tw`absolute inset-0 bg-black bg-opacity-50 items-center justify-center z-50`}>
          <View style={tw`bg-white rounded-2xl mx-6 p-6 shadow-xl`}>
            <Text style={tw`text-xl font-bold text-gray-900 mb-3`}>
              Cancel Subscription
            </Text>
            <Text style={tw`text-gray-600 mb-6 leading-6`}>
              Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.
            </Text>

            <View style={tw`flex-row gap-3`}>
              <TouchableOpacity
                style={tw`flex-1 bg-gray-100 py-3 rounded-lg`}
                onPress={() => setShowCancelConfirm(false)}
              >
                <Text style={tw`text-gray-700 font-semibold text-center`}>
                  Keep Subscription
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={tw`flex-1 bg-red-600 py-3 rounded-lg`}
                onPress={handleCancelConfirm}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator color="white" size="small" />
                ) : (
                  <Text style={tw`text-white font-semibold text-center`}>
                    Cancel
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </SafeAreaView>
  );
};

// Plan Card Component
interface PlanCardProps {
  plan: SubscriptionPlan;
  selected: boolean;
  onSelect: () => void;
  billingInterval: 'monthly' | 'annual';
  isPreviewMode?: boolean;
  isCurrentPlan?: boolean;
}

const PlanCard: React.FC<PlanCardProps> = ({ 
  plan, 
  selected, 
  onSelect, 
  billingInterval, 
  isPreviewMode,
  isCurrentPlan 
}) => {
  const price = billingInterval === 'annual' ? plan.price.annual : plan.price.monthly;
  const savings = billingInterval === 'annual' ? Math.round(((plan.price.monthly * 12 - plan.price.annual) / (plan.price.monthly * 12)) * 100) : 0;
  
  return (
    <TouchableOpacity 
      style={[
        tw`bg-white p-6 rounded-lg mb-4 border-2`,
        selected ? tw`border-blue-500 shadow-lg` : tw`border-gray-200`,
        plan.popular && tw`border-green-500`
      ]}
      onPress={onSelect}
    >
      {/* Popular Badge */}
      {plan.popular && (
        <View style={tw`absolute -top-3 left-4 bg-green-500 px-3 py-1 rounded-full`}>
          <Text style={tw`text-white text-xs font-bold`}>{uiText('mostPopular')}</Text>
        </View>
      )}

      {/* Current Plan Badge */}
      {isCurrentPlan && (
        <View style={tw`absolute -top-3 right-4 bg-blue-500 px-3 py-1 rounded-full`}>
          <Text style={tw`text-white text-xs font-bold`}>{uiText('currentPlanBadge')}</Text>
        </View>
      )}

      {/* Preview Badge */}
      {isPreviewMode && (
        <View style={tw`absolute top-4 right-4 bg-blue-100 px-2 py-1 rounded`}>
          <Text style={tw`text-blue-700 text-xs font-medium`}>{uiText('preview')}</Text>
        </View>
      )}
      
      {/* Plan Header */}
      <View style={tw`mb-4`}>
        <Text style={tw`text-xl font-bold text-gray-800 mb-1`}>{plan.name}</Text>
        <Text style={tw`text-gray-600 text-sm`}>{plan.description}</Text>
      </View>
      
      {/* Pricing */}
      <View style={tw`flex-row items-baseline mb-6`}>
        <Text style={tw`text-3xl font-bold text-blue-600`}>${price}</Text>
        <Text style={tw`text-gray-500 ml-1`}>
          /{billingInterval === 'annual' ? 'year' : 'month'}
        </Text>
        {billingInterval === 'annual' && savings > 0 && (
          <View style={tw`ml-2 bg-green-100 px-2 py-1 rounded`}>
            <Text style={tw`text-green-700 text-xs font-medium`}>Save {savings}%</Text>
          </View>
        )}
      </View>
      
      {/* Features List */}
      <View style={tw`gap-3`}>
        {plan.features.map((feature: string, index: number) => (
          <View key={index} style={tw`flex-row items-center`}>
            <Ionicons name="checkmark-circle" size={20} color="#10b981" />
            <Text style={tw`text-gray-700 ml-3 flex-1`}>
              {getFeatureDisplayName(feature)}
            </Text>
          </View>
        ))}
      </View>

      {/* Limits Display */}
      {plan.limits.members && (
        <View style={tw`mt-4 pt-4 border-t border-gray-100`}>
          <Text style={tw`text-gray-500 text-sm font-medium mb-2`}>{uiText('planLimits')}</Text>
          <Text style={tw`text-gray-600 text-sm`}>
            • Up to {plan.limits.members === -1 ? 'unlimited' : plan.limits.members} members
          </Text>
        </View>
      )}

      {/* Select Plan Button */}
      {!isCurrentPlan && (
        <View style={tw`mt-6 pt-4 border-t border-gray-100`}>
          <View
            style={[
              tw`py-4 rounded-lg`,
              selected ? tw`bg-blue-600` : tw`bg-blue-100`
            ]}
          >
            <Text style={[
              tw`font-bold text-center text-base`,
              selected ? tw`text-white` : tw`text-blue-600`
            ]}>
              {selected ? uiText('selected') : uiText('selectPlan')}
            </Text>
          </View>
        </View>
      )}
    </TouchableOpacity>
  );
};

// Helper function to get user-friendly feature names
const getFeatureDisplayName = (feature: string): string => {
  const displayNames: Record<string, string> = {
    'unlimited_quiz': uiText('featUnlimitedQuiz'),
    'ad_free_experience': uiText('featAdFree'),
    'unlimited_quiz_history': uiText('featHistory'),
    'multiple_question_formats': uiText('featFormats'),
    'multi_language_support': uiText('featLanguages'),
    'multiplayer_create': uiText('featMpCreate'),
    'multiplayer_join': uiText('featMpJoin'),
    'family_analytics': uiText('featFamilyAnalytics'),
    'team_management': uiText('featTeam', { count: PREMIUM_FAMILY_MEMBER_LIMIT }),
    'premium_quiz_packs': uiText('featPacks'),
  };
  
  return displayNames[feature] || feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export default SubscriptionPlansScreen;
